import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Ship, Package, Globe, CheckCircle, Clock, Shield, FileText } from "lucide-react";
import heroPort from "@/assets/hero-port.jpg";

const OceanFreight = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        {/* Hero Section */}
        <section className="relative h-[60vh] mb-16 rounded-lg overflow-hidden mx-4">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroPort})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/60" />
          </div>
          <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-primary-foreground mb-6">
                Ocean Freight Services
              </h1>
              <p className="text-xl text-primary-foreground/90 max-w-2xl mb-8">
                Reliable sea freight solutions connecting USA to the world. FCL, LCL, and specialized cargo services with comprehensive tracking and documentation support.
              </p>
              <Button size="lg" onClick={() => setQuoteModalOpen(true)}>
                Get Ocean Freight Quote
              </Button>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-16">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Comprehensive Ocean Shipping Solutions</h2>
              <p className="text-lg text-muted-foreground">
                With our corporate headquarters strategically positioned in California, we offer unparalleled access to global shipping networks. Our ocean freight services combine decades of maritime expertise with modern technology to deliver your cargo safely and efficiently across all major trade lanes.
              </p>
            </div>
          </section>

          {/* Service Types */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Service Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <Ship className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Full Container Load (FCL)</h3>
                  <p className="text-muted-foreground mb-6">
                    Exclusive container use for your cargo. Ideal for large shipments requiring dedicated space. Available in 20', 40', and 40' High Cube containers with various specifications.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Direct port-to-port service", "Priority booking on major carriers", "Reduced handling and transit time", "Better cargo security", "Cost-effective for bulk shipments"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Request FCL Quote</Button>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardContent className="p-8">
                  <Package className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Less than Container Load (LCL)</h3>
                  <p className="text-muted-foreground mb-6">
                    Share container space with other shipments. Perfect for smaller volumes where a full container isn't necessary. Weekly consolidation services from major European ports.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {["Pay only for space used", "Weekly sailing schedules", "Professional cargo consolidation", "Competitive pricing for small volumes", "Full tracking and documentation"].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => setQuoteModalOpen(true)}>Request LCL Quote</Button>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Process Timeline */}
          <section className="mb-16 bg-secondary rounded-lg p-12">
            <h2 className="text-3xl font-bold text-center mb-12">Our Shipping Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {[
                { step: "1", title: "Quote Request", desc: "Submit your requirements" },
                { step: "2", title: "Booking", desc: "Confirm space reservation" },
                { step: "3", title: "Documentation", desc: "Prepare shipping docs" },
                { step: "4", title: "Origin Handling", desc: "Container loading & customs" },
                { step: "5", title: "Ocean Transit", desc: "Real-time tracking" },
                { step: "6", title: "Destination", desc: "Delivery & clearance" },
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Ocean Freight</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <Globe className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Global Network</h3>
                  <p className="text-muted-foreground">
                    Partnerships with all major carriers including Maersk, MSC, CMA CGM, and Hapag-Lloyd ensuring space availability and competitive rates.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Cargo Insurance</h3>
                  <p className="text-muted-foreground">
                    Comprehensive marine insurance options available. Protect your investment with all-risk coverage and experienced claims support.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Transit Times</h3>
                  <p className="text-muted-foreground">
                    Optimized routing for fastest delivery. Los Angeles to Shanghai: 12-15 days. Los Angeles to Tokyo: 11-14 days. Real-time ETA updates.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Documentation</h3>
                  <p className="text-muted-foreground">
                    Complete bill of lading, certificate of origin, packing lists, and all required shipping documents prepared by our team.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Customs Clearance</h3>
                  <p className="text-muted-foreground">
                    Expert customs brokerage at origin and destination. HS classification, duty calculation, and regulatory compliance handled.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Ship className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-3">Special Equipment</h3>
                  <p className="text-muted-foreground">
                    Reefers, flat racks, open tops, and tank containers available. Temperature-controlled shipments with continuous monitoring.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Major Trade Routes from Los Angeles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { route: "Los Angeles → Tokyo", time: "11-14 days", frequency: "Daily" },
                { route: "Los Angeles → Shanghai", time: "12-15 days", frequency: "Daily" },
                { route: "Los Angeles → Rotterdam", time: "22-26 days", frequency: "3x/week" },
                { route: "Los Angeles → Sydney", time: "18-22 days", frequency: "Weekly" },
                { route: "Los Angeles → Singapore", time: "16-20 days", frequency: "Weekly" },
                { route: "Los Angeles → Hamburg", time: "24-28 days", frequency: "Weekly" },
              ].map((route, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-3">{route.route}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transit Time:</span>
                        <span className="font-semibold">{route.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Frequency:</span>
                        <span className="font-semibold">{route.frequency}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-primary text-primary-foreground rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Ship Your Cargo?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Get a personalized ocean freight quote from our team. Competitive rates, reliable service, and complete peace of mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)}>
                Request Detailed Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Contact Ocean Freight Team
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="ocean-freight" />
    </div>
  );
};

export default OceanFreight;
